import React, {
    forwardRef, useImperativeHandle, useMemo, useState, useCallback,
} from 'react';
import {
    Box, Typography, TextField, FormControlLabel, Switch, Button, Tooltip,
} from '@mui/material';
import { Fullscreen as FullscreenIcon } from '@mui/icons-material';
import MSAModulePage from './MSAModulePage';
import CategoricalGrid from '../CategoricalGrid';
import GuidedEntry from '../GuidedEntry';

// ---------------------------------------------------------------------------
// AttributeAgreementModule — entry UI + Run-Study wrapper for the Attribute
// Agreement Analysis study (studyType "attribute"). Produces kappa statistics
// (within-appraiser, between-appraiser, and — when a known standard is supplied
// — appraiser-vs-standard) on categorical (e.g. Pass/Fail) ratings.
//
// The wrapper (MSAModulePage) owns the Study Description + Run Study lifecycle;
// this file only authors the module-specific input UI. The input sub-component
// is a forwardRef exposing buildInputs() via useImperativeHandle.
// ---------------------------------------------------------------------------

const inputSx = {
    '& .MuiOutlinedInput-root': {
        color: 'rgba(255, 255, 255, 0.9)',
        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
        '&.Mui-focused fieldset': { borderColor: 'rgba(144, 202, 249, 0.7)' },
    },
    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'rgba(144, 202, 249, 0.9)' },
};

const DEFAULT_CATEGORIES_TEXT = 'Pass, Fail';

// Parse the comma-separated Categories field: trim, drop empties, dedupe
// (case-sensitive — order matters, index 0 is "first"). Preserves entry order.
function parseCategories(text) {
    const seen = new Set();
    const out = [];
    String(text).split(',').forEach((raw) => {
        const c = raw.trim();
        if (c && !seen.has(c)) {
            seen.add(c);
            out.push(c);
        }
    });
    return out;
}

// Clamp a raw config text field to an integer >= min (falls back to `fallback`
// so the grid never collapses while the user is mid-typing).
function toInt(raw, min, fallback) {
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n < min) return fallback;
    return n;
}

// Stable row key for a (part, operator, trial) cell.
function rowKey(part, operator, trial) {
    return `${part} ${operator} ${trial}`;
}

// Build the full (part × operator × trial) row list in a STABLE order
// (part outer, operator middle, trial inner), preserving any already-entered
// rating whose (part,operator,trial) key still exists.
function buildRows(numParts, numOperators, numTrials, prevRows) {
    const prevByKey = new Map();
    (prevRows || []).forEach((r) => {
        prevByKey.set(rowKey(r.part, r.operator, r.trial), r.rating);
    });

    const rows = [];
    for (let p = 1; p <= numParts; p += 1) {
        const part = String(p);
        for (let o = 1; o <= numOperators; o += 1) {
            const operator = `Op${o}`;
            for (let t = 1; t <= numTrials; t += 1) {
                const trial = t;
                const key = rowKey(part, operator, trial);
                rows.push({
                    _key: key,
                    part,
                    operator,
                    trial,
                    rating: prevByKey.has(key) ? prevByKey.get(key) : undefined,
                });
            }
        }
    }
    return rows;
}

const gridColumns = [
    { key: 'part',     label: 'Part',     type: 'readonly', width: 64,  align: 'center' },
    { key: 'operator', label: 'Operator', type: 'readonly', width: 88,  align: 'center' },
    { key: 'trial',    label: 'Trial',    type: 'readonly', width: 64,  align: 'center' },
    { key: 'rating',   label: 'Rating',   type: 'category', align: 'left' },
];

export const AttributeAgreementInputs = forwardRef(function AttributeAgreementInputs({ disabled }, ref) {
    const [numPartsText, setNumPartsText]         = useState('5');
    const [numOperatorsText, setNumOperatorsText] = useState('3');
    const [numTrialsText, setNumTrialsText]       = useState('2');
    const [categoriesText, setCategoriesText]     = useState(DEFAULT_CATEGORIES_TEXT);

    const [useStandard, setUseStandard] = useState(false);
    // referenceRatings keyed by String(part) → rating label.
    const [referenceRatings, setReferenceRatings] = useState({});

    const [rows, setRows] = useState(() => buildRows(5, 3, 2, null));

    // Guided (fullscreen sequential) rating entry
    const [guidedMode, setGuidedMode]     = useState(false);
    const [guidedSlots, setGuidedSlots]   = useState([]);
    const [guidedValues, setGuidedValues] = useState([]); // parallel to guidedSlots

    const numParts     = toInt(numPartsText, 2, 5);
    const numOperators = toInt(numOperatorsText, 2, 3);
    const numTrials    = toInt(numTrialsText, 2, 2);

    const categories = useMemo(() => parseCategories(categoriesText), [categoriesText]);
    // Category list actually used for entry — needs >=2 to be valid; fall back
    // to the default so the grid always has usable toggles while typing.
    const gridCategories = categories.length >= 2 ? categories : ['Pass', 'Fail'];

    // Regenerate rows whenever the config changes, preserving existing ratings.
    // We key off the numeric config so partial/invalid text doesn't thrash rows.
    const regenerate = useCallback((np, no, nt) => {
        setRows((prev) => buildRows(np, no, nt, prev));
    }, []);

    const handleConfigChange = (setter, raw, min, fallback, which) => {
        setter(raw);
        const next = toInt(raw, min, fallback);
        const np = which === 'parts' ? next : numParts;
        const no = which === 'operators' ? next : numOperators;
        const nt = which === 'trials' ? next : numTrials;
        regenerate(np, no, nt);
    };

    const handleCellChange = (rowIndex, key, value) => {
        setRows((prev) => {
            const next = prev.slice();
            next[rowIndex] = { ...next[rowIndex], [key]: value };
            return next;
        });
    };

    const handleReferenceChange = (part, value) => {
        setReferenceRatings((prev) => ({ ...prev, [String(part)]: value }));
    };

    // Prune ratings + reference ratings orphaned by a category-label change so the
    // grid matches state — but only once the user has a valid (>=2) set, so
    // mid-typing (where the grid falls back to Pass/Fail) doesn't wipe ratings.
    const handleCategoriesChange = (raw) => {
        setCategoriesText(raw);
        const parsed = parseCategories(raw);
        if (parsed.length >= 2) {
            const valid = new Set(parsed);
            setRows((prev) => prev.map((r) => (r.rating && !valid.has(r.rating) ? { ...r, rating: undefined } : r)));
            setReferenceRatings((prev) => {
                const next = {};
                Object.entries(prev).forEach(([k, v]) => { if (valid.has(v)) next[k] = v; });
                return next;
            });
        }
    };

    // --- Guided entry (categorical) ---------------------------------------------
    const handleEnterGuided = useCallback(() => {
        const slots = rows.map((r, i) => ({ label: `Part ${r.part} / ${r.operator} / Trial ${r.trial}`, rowIndex: i }));
        setGuidedSlots(slots);
        setGuidedValues(rows.map((r) => r.rating ?? null));
        setGuidedMode(true);
    }, [rows]);

    const handleGuidedSubmitValue = useCallback((cat, idx) => {
        setGuidedValues((prev) => { const next = prev.slice(); next[idx] = cat; return next; });
    }, []);

    const handleExitGuided = useCallback(() => {
        setRows((prev) => {
            const next = prev.slice();
            guidedSlots.forEach((slot, i) => {
                const v = guidedValues[i];
                if (v !== null && v !== undefined && next[slot.rowIndex]) {
                    next[slot.rowIndex] = { ...next[slot.rowIndex], rating: v };
                }
            });
            return next;
        });
        setGuidedMode(false);
    }, [guidedSlots, guidedValues]);

    useImperativeHandle(ref, () => ({
        buildInputs() {
            // --- config ---
            if (numParts < 2)     throw new Error('Number of parts must be at least 2.');
            if (numOperators < 2) throw new Error('Number of operators must be at least 2.');
            if (numTrials < 2)    throw new Error('Number of trials must be at least 2.');

            // --- categories ---
            const cats = parseCategories(categoriesText);
            if (cats.length < 2) {
                throw new Error('Enter at least two distinct categories (comma-separated), e.g. "Pass, Fail".');
            }
            const catSet = new Set(cats);

            // --- assessments (every grid row must be rated) ---
            const unrated = rows.filter((r) => !r.rating || !catSet.has(r.rating)).length;
            if (unrated > 0) {
                throw new Error(`Enter a rating for every row (${unrated} remaining).`);
            }

            const assessments = rows.map((r) => {
                const trial = parseInt(r.trial, 10);
                if (!Number.isInteger(trial) || trial < 1) {
                    throw new Error('Trial numbers must be positive integers.');
                }
                return {
                    part:     String(r.part),
                    operator: String(r.operator),
                    trial,
                    rating:   r.rating,
                };
            });

            const payload = {
                assessments,
                categories: cats,
                numParts,
                numOperators,
                numTrials,
            };

            // --- reference ratings: include ONLY when the toggle is on AND every
            // part has a standard set to a valid category. ---
            if (useStandard) {
                const ref = {};
                for (let p = 1; p <= numParts; p += 1) {
                    const partKey = String(p);
                    const val = referenceRatings[partKey];
                    if (!val || !catSet.has(val)) {
                        throw new Error(`Set a known standard rating for every part (part ${partKey} is missing).`);
                    }
                    ref[partKey] = val;
                }
                payload.referenceRatings = ref;
            }

            return payload;
        },
        // Re-populate config + ratings grid from a persisted engine input (history reload).
        hydrate(input) {
            if (!input || typeof input !== 'object') return;
            const np = Number(input.numParts) || 5;
            const no = Number(input.numOperators) || 3;
            const nt = Number(input.numTrials) || 2;
            setNumPartsText(String(np));
            setNumOperatorsText(String(no));
            setNumTrialsText(String(nt));
            if (Array.isArray(input.categories) && input.categories.length) {
                setCategoriesText(input.categories.join(', '));
            }
            const ratingByKey = new Map();
            (input.assessments || []).forEach((a) => {
                ratingByKey.set(rowKey(String(a.part), String(a.operator), a.trial), a.rating);
            });
            setRows(buildRows(np, no, nt, null).map((r) => {
                const rating = ratingByKey.get(r._key);
                return rating !== undefined ? { ...r, rating } : r;
            }));
            const refs = input.referenceRatings && typeof input.referenceRatings === 'object' ? input.referenceRatings : {};
            setReferenceRatings(refs);
            setUseStandard(Object.keys(refs).length > 0);
        },
    }));

    const configFieldSx = { ...inputSx, width: { xs: '100%', sm: 130 } };

    if (guidedMode) {
        return (
            <GuidedEntry
                contextLabel="Attribute Agreement — Guided Rating"
                slots={guidedSlots}
                values={guidedValues}
                categories={gridCategories}
                onSubmitValue={handleGuidedSubmitValue}
                onClose={handleExitGuided}
                backLabel="Back to Grid"
            />
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Config row */}
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <TextField
                    label="Parts"
                    size="small"
                    type="number"
                    value={numPartsText}
                    onChange={(e) => handleConfigChange(setNumPartsText, e.target.value, 2, 5, 'parts')}
                    disabled={disabled}
                    sx={configFieldSx}
                    inputProps={{ inputMode: 'numeric', min: 2, step: 1 }}
                    helperText="≥ 2"
                    FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.4)', m: 0, mt: 0.25 } }}
                />
                <TextField
                    label="Operators"
                    size="small"
                    type="number"
                    value={numOperatorsText}
                    onChange={(e) => handleConfigChange(setNumOperatorsText, e.target.value, 2, 3, 'operators')}
                    disabled={disabled}
                    sx={configFieldSx}
                    inputProps={{ inputMode: 'numeric', min: 2, step: 1 }}
                    helperText="≥ 2"
                    FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.4)', m: 0, mt: 0.25 } }}
                />
                <TextField
                    label="Trials"
                    size="small"
                    type="number"
                    value={numTrialsText}
                    onChange={(e) => handleConfigChange(setNumTrialsText, e.target.value, 2, 2, 'trials')}
                    disabled={disabled}
                    sx={configFieldSx}
                    inputProps={{ inputMode: 'numeric', min: 2, step: 1 }}
                    helperText="≥ 2"
                    FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.4)', m: 0, mt: 0.25 } }}
                />
                <TextField
                    label="Categories"
                    size="small"
                    value={categoriesText}
                    onChange={(e) => handleCategoriesChange(e.target.value)}
                    disabled={disabled}
                    sx={{ ...inputSx, flex: 1, minWidth: { xs: '100%', sm: 200 } }}
                    placeholder="Pass, Fail"
                    helperText="Comma-separated, ≥ 2 distinct. Order matters."
                    FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.4)', m: 0, mt: 0.25 } }}
                />
            </Box>

            {/* Ratings grid */}
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                        Ratings — one row per part × operator × trial ({rows.length} rows)
                    </Typography>
                    <Tooltip title="Guided fullscreen entry — tap Pass/Fail through every row in order (mobile keypad / keyboard 1–N)">
                        <span>
                            <Button
                                size="small"
                                startIcon={<FullscreenIcon />}
                                onClick={handleEnterGuided}
                                disabled={disabled}
                                sx={{ fontSize: '0.7rem', color: 'rgba(144,202,249,0.7)', py: 0.25, px: 1 }}
                            >
                                Guided Entry
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
                <CategoricalGrid
                    columns={gridColumns}
                    rows={rows}
                    onCellChange={handleCellChange}
                    categories={gridCategories}
                    disabled={disabled}
                    maxHeight={420}
                />
            </Box>

            {/* Known standard */}
            <Box>
                <FormControlLabel
                    control={(
                        <Switch
                            checked={useStandard}
                            onChange={(e) => setUseStandard(e.target.checked)}
                            disabled={disabled}
                            size="small"
                        />
                    )}
                    label="I have known standard ratings (per part)"
                    sx={{
                        m: 0,
                        '& .MuiFormControlLabel-label': { color: 'rgba(255,255,255,0.75)', fontSize: '0.85rem' },
                    }}
                />

                {useStandard && (
                    <Box
                        sx={{
                            mt: 1.5,
                            p: 1.5,
                            backgroundColor: 'rgba(255,255,255,0.02)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            borderRadius: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                        }}
                    >
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                            Known (reference) rating for each part
                        </Typography>
                        <CategoricalGrid
                            columns={[
                                { key: 'part',   label: 'Part',              type: 'readonly', width: 64, align: 'center' },
                                { key: 'rating', label: 'Standard Rating',   type: 'category', align: 'left' },
                            ]}
                            rows={Array.from({ length: numParts }, (_, i) => ({
                                part:   String(i + 1),
                                rating: referenceRatings[String(i + 1)],
                            }))}
                            onCellChange={(rowIndex, key, value) => {
                                if (key === 'rating') handleReferenceChange(rowIndex + 1, value);
                            }}
                            categories={gridCategories}
                            disabled={disabled}
                            maxHeight={280}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
});

export default function AttributeAgreementModule({ onBack }) {
    return (
        <MSAModulePage
            studyType="attribute"
            title="Attribute Agreement Analysis"
            subtitle="Kappa Statistics"
            onBack={onBack}
            descriptionPlaceholder="e.g. Visual go/no-go gauge — 3 inspectors, 2 trials"
            helperText="Each appraiser rates every part multiple times. Kappa quantifies within-appraiser repeatability, between-appraiser reproducibility, and (optionally) agreement with a known standard."
        >
            {({ inputRef, disabled }) => (
                <AttributeAgreementInputs ref={inputRef} disabled={disabled} />
            )}
        </MSAModulePage>
    );
}
